import { randomBytes, createHash } from "node:crypto";
import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";
import { getAgentVersion, type AgentDefinition } from "./foundry-agent-get.js";
import { awaitAgentQueryable } from "./agent-ready.js";
import { readPersonaA2aCard } from "./persona-a2a.js";
import { appendSnippet, stripSnippet } from "./a2a-snippet.js";

export { appendSnippet, stripSnippet };

const FOUNDRY_ARM_API = "2025-04-01-preview";
const FOUNDRY_DATA_SCOPE = "https://ai.azure.com/.default";
const ARM_SCOPE = "https://management.azure.com/.default";
const A2A_TOOLS = ["discover_workers", "invoke_worker", "check_delegation"];

export interface EnableA2aArgs {
  credential: TokenCredential;
  projectEndpoint: string;
  projectArmId: string;
  agentName: string;
  personaPath: string;
  bridgeUrl: string;
  onProgress?: (m: string) => void;
}
export interface EnableA2aResult { mode: "caller" | "target"; connectionName?: string; foundryVersion: string }

export async function enableA2a(args: EnableA2aArgs): Promise<EnableA2aResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});
  const connectionName = `a2a-${args.agentName}`;

  progress("Reading persona a2a-card…");
  const { serialized: a2aCardJson } = readPersonaA2aCard(args.personaPath);

  progress("Waiting for the agent to become queryable…");
  await awaitAgentQueryable({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
    onProgress: args.onProgress,
  });

  progress("Reading current agent definition…");
  const current = await getAgentVersion({ credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName });

  if (current.definition.kind === "hosted") {
    // Hosted agents are callees only — they can't call the bridge, so they get NO
    // caller machinery (no bearer, no connection, no a2a tool, no snippet). We only
    // register them as a discoverable TARGET: set a2a + a2aCard metadata and clone
    // the definition unchanged (preserving the container spec).
    progress("Registering the hosted target (metadata only)…");
    const metadata: Record<string, string> = { ...(current.metadata ?? {}), a2a: "true", a2aCard: a2aCardJson };
    // Hosted-agent version operations are preview-gated — the data-plane rejects
    // them (403 preview_feature_required) without this header.
    const foundryVersion = await createVersion({ credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName, definition: current.definition, metadata, extraHeaders: { "Foundry-Features": "HostedAgents=V1Preview" } });
    return { mode: "target", foundryVersion };
  }
  // After the hosted-agent early return above, kind is narrowed to "prompt" here.
  // The check below is a runtime guard for future kinds that might be added
  // before the type union is updated; casting to string is safe for the check and message.
  if ((current.definition.kind as string) !== "prompt") {
    throw new LocalCliError({
      code: "A2A_NOT_PROMPT",
      message: `Agent '${args.agentName}' is kind '${current.definition.kind as string}'. Agent-to-agent enablement supports prompt agents (callers) and hosted agents (callees); '${current.definition.kind as string}' is neither.`,
    });
  }

  const bearer = `a2a_${randomBytes(32).toString("base64url")}`;
  const bearerHash = createHash("sha256").update(bearer).digest("hex");

  progress("Provisioning the A2A connection…");
  await putA2aConnection({ credential: args.credential, projectArmId: args.projectArmId, connectionName, target: args.bridgeUrl, bearer });

  progress("Deploying the a2a-enabled agent version…");
  const definition: AgentDefinition = {
    ...current.definition,
    instructions: appendSnippet(current.definition.instructions ?? ""),
    tools: withA2aTool(current.definition.tools ?? [], args.bridgeUrl, connectionName),
  };
  const metadata: Record<string, string> = {
    ...(current.metadata ?? {}),
    a2a: "true",
    a2aCard: a2aCardJson,
    a2aBearerHash: bearerHash,
  };
  const foundryVersion = await createVersion({ credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName, definition, metadata });
  return { mode: "caller", connectionName, foundryVersion };
}

export interface DisableA2aArgs {
  credential: TokenCredential;
  projectEndpoint: string;
  projectArmId: string;
  agentName: string;
  onProgress?: (m: string) => void;
}

export async function disableA2a(args: DisableA2aArgs): Promise<{ connectionName: string; foundryVersion?: string }> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});
  const connectionName = `a2a-${args.agentName}`;
  const current = await getAgentVersion({ credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName });

  progress("Removing the a2a tool + metadata…");
  const tools = (current.definition.tools ?? []).filter(
    (t) => !((t as { type?: string }).type === "mcp" && (t as { server_label?: string }).server_label === "a2a"),
  );
  const definition: AgentDefinition = { ...current.definition, instructions: stripSnippet(current.definition.instructions ?? ""), tools };
  const metadata: Record<string, string> = { ...(current.metadata ?? {}) };
  delete metadata.a2a;
  delete metadata.a2aCard;
  delete metadata.a2aBearerHash;
  const foundryVersion = await createVersion({ credential: args.credential, projectEndpoint: args.projectEndpoint, agentName: args.agentName, definition, metadata });

  progress("Deleting the A2A connection…");
  await deleteConnection({ credential: args.credential, projectArmId: args.projectArmId, connectionName }); // best-effort
  return { connectionName, foundryVersion };
}

// ──── pure helpers (exported for tests) ───────────────────────────────────────

export function withA2aTool(
  tools: NonNullable<AgentDefinition["tools"]>,
  bridgeUrl: string,
  connectionName: string,
): NonNullable<AgentDefinition["tools"]> {
  const others = tools.filter(
    (t) => !((t as { type?: string }).type === "mcp" && (t as { server_label?: string }).server_label === "a2a"),
  );
  return [
    ...others,
    { type: "mcp", server_label: "a2a", server_url: bridgeUrl, allowed_tools: A2A_TOOLS, require_approval: "never", project_connection_id: connectionName },
  ];
}

// ──── ARM / Foundry calls ─────────────────────────────────────────────────────

async function putA2aConnection(args: { credential: TokenCredential; projectArmId: string; connectionName: string; target: string; bearer: string }): Promise<void> {
  const token = await args.credential.getToken(ARM_SCOPE);
  if (!token?.token) throw new LocalCliError({ code: "ARM_AUTH", message: "Could not acquire ARM token" });
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  const body = JSON.stringify({
    properties: {
      authType: "CustomKeys",
      category: "CustomKeys",
      target: args.target,
      isSharedToAll: false,
      credentials: { keys: { Authorization: `Bearer ${args.bearer}` } },
      metadata: { managedBy: "m8t-a2a" },
    },
  });
  const res = await fetch(url, { method: "PUT", headers: { Authorization: `Bearer ${token.token}`, "Content-Type": "application/json" }, body });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({ code: "A2A_CONN_PUT_FAILED", message: `PUT ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 300)}` });
  }
}

async function deleteConnection(args: { credential: TokenCredential; projectArmId: string; connectionName: string }): Promise<void> {
  const token = await args.credential.getToken(ARM_SCOPE);
  if (!token?.token) return;
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- best-effort delete; errors are intentionally swallowed
  await fetch(url, { method: "DELETE", headers: { Authorization: `Bearer ${token.token}` } }).catch(() => {});
}

async function createVersion(args: { credential: TokenCredential; projectEndpoint: string; agentName: string; definition: AgentDefinition; metadata: Record<string, string>; extraHeaders?: Record<string, string> }): Promise<string> {
  const token = await args.credential.getToken(FOUNDRY_DATA_SCOPE);
  if (!token?.token) throw new LocalCliError({ code: "FOUNDRY_AUTH", message: "Could not acquire Foundry data-plane token" });
  const url = `${args.projectEndpoint}/agents/${args.agentName}/versions?api-version=v1`;
  const res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token.token}`, "Content-Type": "application/json", ...(args.extraHeaders ?? {}) }, body: JSON.stringify({ definition: args.definition, metadata: args.metadata }) });
  if (!res.ok) {
    const text = await res.text();
    throw new LocalCliError({ code: "A2A_CREATE_VERSION_FAILED", message: `POST ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 500)}` });
  }
  const data = (await res.json()) as { version?: string };
  if (!data.version) throw new LocalCliError({ code: "A2A_CREATE_VERSION_NO_VERSION", message: "createVersion returned no version" });
  return data.version;
}
